import React, { useState, useEffect } from "react";
import api from "../services/api";

const DivisionManagement = () => {
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingDivision, setEditingDivision] = useState(null);
  const [formData, setFormData] = useState({ division_name: "" });

  useEffect(() => {
    loadDivisions();
  }, []);

  const loadDivisions = async () => {
    setLoading(true);
    try {
      const response = await api.get("/divisions");
      setDivisions(response.data.data || []);
    } catch (error) {
      console.error("Error loading divisions:", error);
      alert(
        "Error loading divisions: " +
          (error.response?.data?.message || error.message)
      );
    }
    setLoading(false);
  };

  const handleCreateOrUpdateDivision = async (e) => {
    e.preventDefault();
    if (!formData.division_name.trim()) {
      alert("Division name is required!");
      return;
    }
    setSaving(true);
    try {
      if (editingDivision) {
        await api.put(`/divisions/${editingDivision.id}`, {
          division_name: formData.division_name,
        });
        alert("Division updated successfully!");
      } else {
        await api.post("/divisions", { division_name: formData.division_name });
        alert("Division created successfully!");
      }
      setFormData({ division_name: "" });
      setShowForm(false);
      setEditingDivision(null);
      loadDivisions();
    } catch (error) {
      console.error("Error saving division:", error);
      alert(error.response?.data?.message || "Error saving division");
    }
    setSaving(false);
  };

  const handleEdit = (division) => {
    setEditingDivision(division);
    setFormData({ division_name: division.division_name });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this division?")) {
      return;
    }
    setLoading(true);
    try {
      await api.delete(`/divisions/${id}`);
      alert("Division deleted successfully!");
      loadDivisions();
    } catch (error) {
      console.error("Error deleting division:", error);
      alert(error.response?.data?.message || "Error deleting division");
    } finally {
      setLoading(false);
    }
  };

  if (loading && divisions.length === 0) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Division Management</h2>
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingDivision(null);
              setFormData({ division_name: "" });
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            {showForm ? "Cancel" : "+ Create Division"}
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4">
                {editingDivision ? "Edit Division" : "Create New Division"}
              </h3>
              <form
                onSubmit={handleCreateOrUpdateDivision}
                className="space-y-4"
              >
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Division Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.division_name}
                    onChange={(e) =>
                      setFormData({ division_name: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter division name (e.g., Panchavati, New Nashik, Nashik Road)"
                  />
                </div>
                <div className="col-span-2 flex gap-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving
                      ? "Saving..."
                      : editingDivision
                      ? "Update Division"
                      : "Create Division"}
                  </button>
                  <button
                    type="submit"
                    onClick={() => {
                      setShowForm(false);
                    }}
                    className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 disabled:opacity-50"
                  >
                    cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-teal-600 to-emerald-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Division Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {divisions.length === 0 ? (
                <tr>
                  <td
                    colSpan="3"
                    className="px-6 py-8 text-center text-sm text-gray-500"
                  >
                    No divisions found. Click "+ Create Division" to add a new
                    division.
                  </td>
                </tr>
              ) : (
                divisions.map((division) => (
                  <tr key={division.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {division.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {division.division_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(division)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
                          title="Edit Division"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(division.id)}
                          className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                          title="Delete Division"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DivisionManagement;
